import PolicyBenefits from './Components/PolicyBenefits'
import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState, useMemo } from 'react'
import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import { faInfoCircle, faPlus, faCheck, faSpinner, faUserShield, faCircleXmark, faSchool, faFile } from '@fortawesome/free-solid-svg-icons'
import SideBar from '../../SideBar'
import moment from 'moment';
import useAuth from '../../../hooks/useAuth'
import { getSaiTerminology } from '../../../config/terminology'

const PolicyInfo = () => {
  const axios = useAxiosPrivate()
  const { auth } = useAuth()
  const [policyData, setPolicyData] = useState(null)
  const terms = getSaiTerminology(policyData)
  const columns = terms.policyInfo.columns
  const [orgType, setOrgType] = useState(null)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [showBenefitsModal, setShowBenefitsModal] = useState(false)
  const openUpgrade = () => setIsUpgradeModalOpen(true)
  const closeUpgrade = () => setIsUpgradeModalOpen(false)
  const [items, setItems] = useState([])
  const toggleModal = () => {
    setShowBenefitsModal(!showBenefitsModal)
  }

  function safeParse(json) {
    try { return json ? JSON.parse(json) : null; } catch { return null; }
  }

  const policyInfo = useMemo(
    () => safeParse(policyData?.policy_information) ?? {},
    [policyData]
  );

  useEffect(() => {
    setPolicyData(JSON.parse(localStorage.getItem('policyData')));
    setOrgType(JSON.parse(localStorage.getItem('orgType')));

    if (policyData?.policy_id) {
      setItems(policyInfo?.items || []);
    }
    console.log(items)
  }, [policyData?.policy_id])

  const totals = useMemo(() => {
    return items.reduce(
      (accumulator, item) => {
        const num = (value) => (value == null || value === '' ? 0 : Number(value))
        accumulator.purchasedFte += num(item.fte);
        accumulator.submittedFte += num(item.submitted ?? item.submitted_sc);
        accumulator.maternityFte += num(item.maternity)
        return accumulator
      },
      { purchasedFte: 0, submittedFte: 0, maternityFte: 0 }
    )
  }, [items])

  const totalQuotedNumber = React.useMemo(() => {
    const items = policyInfo?.items ?? []
    return items.reduce((sum, item) => {
      const quote = item?.quoted != null ? parseFloat(item.quoted) : 0
      return sum + (Number.isFinite(quote) ? quote : 0)
    }, 0)
  }, [policyInfo?.items])

  const totalQuotedDisplay = React.useMemo(() => {
    let formatted = totalQuotedNumber.toFixed(2)
    if (totalQuotedNumber < 10) {
      formatted = formatted.replace(/^0+/, '')
    }
    return formatted
  }, [totalQuotedNumber])

  const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : '—')
  const fmt = number => Number(number).toFixed(2)

  return (
    <div className="w-full">
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg relative">
            <button
              onClick={closeUpgrade}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-600"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
            <h2 className="text-xl font-bold mb-4 text-sas">Contact Us For More Details</h2>
            {policyData?.department === 'Mutual' || policyData?.department === "GBS" ? (
              <a href='tel:01773851079' className="mb-2"><strong>Phone:</strong> 01773 851 079</a>
            ) : (
              <a href='tel:0177381440' className="mb-2"><strong>Phone:</strong> 01773 814 400</a>
            )}
            {policyData?.department === 'Mutual' || policyData?.department === "GBS" ? (
              <p className="mb-2"><strong>Email:</strong> <a href="mailto:enquiries@schoolsmutual.co.uk" className="text-sas underline">enquiries@schoolsmutual.co.uk</a></p>
            ) : (
              <p className="mb-2"><strong>Email:</strong> <a href="mailto:support@schoolsadvisoryservice.co.uk" className="text-sas underline">support@schoolsadvisoryservice.co.uk</a></p>
            )}
            <p className="text-sm text-gray-600 mt-4">Our team will be happy to assist with upgrades and optional extras.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6 mt-2">
        <main className="w-full space-y-4 text-sas-dark">
          <div className='flex items-center w-full space-x-4'>
            <div className='bg-white rounded-lg shadow-md w-full flex items-center'>
              <div className='bg-pink-100 py-4 px-6 rounded-l-lg'> <FontAwesomeIcon icon={faSchool} className='text-pink-500 h-7 w-7' /></div>
              <div className='pl-4'>
                <div className='text-sm'>School</div>
                <div>{orgType?.name}</div>
              </div>
            </div>
            <div className='bg-white rounded-lg shadow-md w-full flex items-center'>
              <div className='bg-pink-100 py-4 px-6 rounded-l-lg'> <FontAwesomeIcon icon={faFile} className='text-pink-500 h-7 w-7' /></div>
              <div className='pl-4'>
                <div className='text-sm'>{terms.policy.singularTitle} Type</div>
                <div>{policyData?.product}</div>
              </div>
            </div>
          </div>
          <div className='w-full space-y-2'>
            <div className='font-semibold text-xl'>{terms.policy.singularTitle} Cover Summary</div>
            {/* General Information */}
            <div className='bg-white rounded-lg shadow-md p-4 w-full space-y-1 ' >
              <div className='text-base font-semibold mb-4'>General Information</div>
              <div className='grid grid-cols-3 gap-2'>
                <div className="text-sm">{terms.policy.singularTitle} Reference: <strong>{policyData?.policy_reference || '—'}</strong></div>
                <div className='text-sm'>Cover Start Date: <strong>{formatDate(policyData?.policy_inception)}</strong></div>
                <div className='text-sm'>Cover End Date: <strong>{formatDate(policyData?.policy_expire)}</strong></div>
                <div className='text-sm'>No. of Staff Quoted (FTE): <strong>{totalQuotedDisplay}</strong></div>
                <div className="text-sm">Premium: <strong>£{Number(policyData?.policy_premium || 0).toLocaleString()}</strong></div>
                <div className="text-sm">Account Manager: <strong className="text-sas">{policyData?.policy_owner_name || '—'}</strong></div>
              </div>
            </div>
          </div>
          {/* School Information (per staff type) */}
          <div className='w-full space-y-2'>
            <div className='text-xl font-semibold pt-4 pb-2'>School Information</div>
            <div className="overflow-x-auto rounded-lg bg-white shadow-md">
              <table className="min-w-full text-[12px] text-left text-gray-800 overflow-hidden">
                <thead>
                  <tr className="bg-sas text-white text-sm">
                    {columns.map(({ key, label }) => (
                      <th key={key} className={`p-4 font-semibold ${key === 'staffType' ? 'text-left' : 'text-center'}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, idx) => (
                    <tr key={idx} className="border-b text-center text-sm">
                      {columns.map(({ key, fallbackKey, format }) => {
                        const value = row[key] ?? (fallbackKey ? row[fallbackKey] : undefined)
                        return (
                          <td key={key} className={`px-4 py-2${key === 'staffType' ? ' text-left' : ' text-center'}`}>
                            {format === 'fte' ? fmt(value ?? 0) : value ?? '—'}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                  {items.length > 0 && (
                    <tr className="font-semibold text-center bg-white text-sm">
                      {columns.map(({ key, totalKey }) => (
                        <td key={key} className={`px-4 py-2${key === 'staffType' ? ' text-left' : ' text-center'}`}>
                          {key === 'staffType' ? 'Totals' : totalKey ? fmt(totals[totalKey]) : '—'}
                        </td>
                      ))}
                    </tr>
                  )}
                  {items.length === 0 && (
                    <tr><td className="p-4 text-center text-gray-500" colSpan={columns.length}>No staff type information found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <PolicyBenefits policyData={policyData} onUpgrade={openUpgrade} showSectionHeading />
        </main>
      </div>
    </div>
  )
}

export default PolicyInfo
